import { ReactNode, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, AlertTriangle, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAccessAttemptLogger } from "@/hooks/useAccessAttemptLogger";
import { supabase } from "@/integrations/supabase/client";

interface AdminRoleProtectionProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const AdminRoleProtection = ({ children, fallback }: AdminRoleProtectionProps) => {
  const { isAdmin, loading, error } = useUserRole();
  const navigate = useNavigate();
  const { logAccessAttempt } = useAccessAttemptLogger();

  // Log unauthorized access attempts
  useEffect(() => {
    const logAttempt = async () => {
      if (!loading && !error) {
        const { data: { user } } = await supabase.auth.getUser();
        
        await logAccessAttempt({
          user_id: user?.id,
          page_attempted: 'admin_panel',
          access_granted: isAdmin
        });
      }
    };

    logAttempt();
  }, [isAdmin, loading, error, logAccessAttempt]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-primary animate-pulse" />
            <h2 className="text-xl font-semibold mb-2">Checking Access</h2>
            <p className="text-muted-foreground">Verifying your administrator privileges...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              Access Check Failed
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-red-700">
              Unable to verify your access permissions. Please try again or contact support.
            </p>
            <p className="text-sm text-red-600">{error}</p>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full"
              variant="outline"
            >
              <Home className="w-4 h-4 mr-2" />
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    if (fallback) return <>{fallback}</>;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <Shield className="w-5 h-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-red-700">
              You don't have administrator privileges to access this page.
            </p>
            <p className="text-sm text-red-600">
              This area is restricted to authorized administrators only.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button 
                onClick={() => navigate('/')} 
                className="w-full"
                variant="outline"
              >
                <Home className="w-4 h-4 mr-2" />
                Return Home
              </Button>
              <Button onClick={() => navigate('/admin-login')} className="w-full">
                Sign in as Admin
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};